import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Mic, ArrowLeft, Plus, Edit2, Trash2, Save, X, Target, Building2, Package, Settings, Eye, EyeOff, Upload, Loader2, Phone, Users, CreditCard, History, LogOut, BarChart3, Shield, Sparkles, Download, Telescope } from 'lucide-react';
import { toast } from 'sonner';
import { SupportButton } from '../components/SupportWidget';
import ProductIntelligence from '../components/ProductIntelligence';
import ResearchTemplateEditor from '../components/ResearchTemplateEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('settings');
  
  // Company Settings State
  const [companySettings, setCompanySettings] = useState({
    company_name: 'My Company',
    logo_url: '',
    show_solution_tab: true,
    solution_tab_label: 'Solution',
    primary_color: '#0E4E5A',
    call_type_labels: {
      primary_call: 'Qualification Call',
      follow_up_call: 'Follow-up Call'
    },
    managers_can_view_billing: false
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Frameworks State
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [isEditingFramework, setIsEditingFramework] = useState(false);
  const [isCreatingFramework, setIsCreatingFramework] = useState(false);
  const [frameworkForm, setFrameworkForm] = useState({
    name: '',
    description: '',
    categories: [],
    questions: []
  });
  const [newCategory, setNewCategory] = useState('');
  const [newQuestion, setNewQuestion] = useState({ question: '', category: '', weight: 3 });
  
  // Products State
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    features: [],
    qualification_questions: [],
    department_ids: []
  });
  const [newFeature, setNewFeature] = useState('');
  const [newProductQuestion, setNewProductQuestion] = useState({ question: '', answer_type: 'boolean', weight: 3 });
  const [departments, setDepartments] = useState([]);
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchAll();
  }, [isAdmin, navigate]);

  const fetchAll = async () => {
    await Promise.all([
      fetchCompanySettings(),
      fetchFrameworks(),
      fetchProducts(),
      fetchDepartments()
    ]);
  };

  const fetchCompanySettings = async () => {
    try {
      const { data } = await axios.get(`${API}/company/settings`);
      setCompanySettings(data);
    } catch (e) {
      console.error('Failed to fetch settings:', e);
    }
  };

  const fetchFrameworks = async () => {
    try {
      const { data } = await axios.get(`${API}/frameworks`);
      setFrameworks(data);
    } catch (e) {
      console.error('Failed to fetch frameworks:', e);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products/all`);
      setProducts(data);
    } catch (e) {
      console.error('Failed to fetch products:', e);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data } = await axios.get(`${API}/org/departments`);
      setDepartments(data);
    } catch (e) {
      console.error('Failed to fetch departments:', e);
    }
  };

  // Company Settings handlers
  const saveCompanySettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/company/settings`, companySettings);
      toast.success('Company settings saved');
    } catch (e) {
      console.error('Failed to save settings:', e);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCompanySettings(prev => ({ ...prev, logo_url: data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (err) {
      console.error('Logo upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    setCompanySettings(prev => ({ ...prev, logo_url: null }));
    try {
      await axios.put(`${API}/company/settings`, { logo_url: '' });
      toast.success('Logo removed');
    } catch (e) {
      toast.error('Failed to remove logo');
    }
  };

  // Framework handlers
  const handleSelectFramework = (fw) => {
    setSelectedFramework(fw);
    setFrameworkForm({
      name: fw.name,
      description: fw.description,
      categories: fw.categories || [],
      questions: fw.questions || []
    });
    setIsEditingFramework(false);
  };

  const handleCreateFramework = () => {
    setSelectedFramework(null);
    setFrameworkForm({ name: '', description: '', categories: [], questions: [] });
    setIsCreatingFramework(true);
    setIsEditingFramework(true);
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    setFrameworkForm(prev => ({
      ...prev,
      categories: [...prev.categories, newCategory.trim()]
    }));
    setNewCategory('');
  };

  const handleRemoveCategory = (cat) => {
    setFrameworkForm(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== cat),
      questions: prev.questions.filter(q => q.category !== cat)
    }));
  };

  const handleAddQuestion = () => {
    if (!newQuestion.question.trim() || !newQuestion.category) return;
    const question = {
      id: `q_${Date.now()}`,
      question: newQuestion.question.trim(),
      category: newQuestion.category,
      weight: parseInt(newQuestion.weight) || 3,
      is_active: true
    };
    setFrameworkForm(prev => ({
      ...prev,
      questions: [...prev.questions, question]
    }));
    setNewQuestion({ question: '', category: '', weight: 3 });
  };

  const handleRemoveQuestion = (questionId) => {
    setFrameworkForm(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  const saveFramework = async () => {
    if (!frameworkForm.name.trim() || !frameworkForm.description.trim()) {
      toast.error('Name and description are required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: frameworkForm.name,
        description: frameworkForm.description,
        categories: frameworkForm.categories,
        questions: frameworkForm.questions
      };

      if (isCreatingFramework) {
        await axios.post(`${API}/frameworks`, payload);
        toast.success('Framework created');
      } else {
        await axios.put(`${API}/frameworks/${selectedFramework.id}`, payload);
        toast.success('Framework updated');
      }

      await fetchFrameworks();
      setIsEditingFramework(false);
      setIsCreatingFramework(false);
    } catch (e) {
      console.error('Save failed:', e);
      toast.error('Failed to save framework');
    } finally {
      setLoading(false);
    }
  };

  const deleteFramework = async (frameworkId) => {
    if (!window.confirm('Delete this framework?')) return;
    try {
      await axios.delete(`${API}/frameworks/${frameworkId}`);
      toast.success('Framework deleted');
      setSelectedFramework(null);
      await fetchFrameworks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // Product handlers
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      features: product.features || [],
      qualification_questions: product.qualification_questions || [],
      department_ids: product.department_ids || []
    });
    setIsEditingProduct(false);
  };

  const handleCreateProduct = () => {
    setSelectedProduct(null);
    setProductForm({ name: '', description: '', features: [], qualification_questions: [], department_ids: [] });
    setIsCreatingProduct(true);
    setIsEditingProduct(true);
  };

  const handleAddFeature = () => {
    if (!newFeature.trim()) return;
    setProductForm(prev => ({
      ...prev,
      features: [...prev.features, newFeature.trim()]
    }));
    setNewFeature('');
  };

  const handleRemoveFeature = (index) => {
    setProductForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleAddProductQuestion = () => {
    if (!newProductQuestion.question.trim()) return;
    const question = {
      id: `pq_${Date.now()}`,
      question: newProductQuestion.question.trim(),
      answer_type: newProductQuestion.answer_type,
      weight: parseInt(newProductQuestion.weight) || 3
    };
    setProductForm(prev => ({
      ...prev,
      qualification_questions: [...prev.qualification_questions, question]
    }));
    setNewProductQuestion({ question: '', answer_type: 'boolean', weight: 3 });
  };

  const handleRemoveProductQuestion = (questionId) => {
    setProductForm(prev => ({
      ...prev,
      qualification_questions: prev.qualification_questions.filter(q => q.id !== questionId)
    }));
  };

  const saveProduct = async () => {
    if (!productForm.name.trim() || !productForm.description.trim()) {
      toast.error('Name and description are required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: productForm.name,
        description: productForm.description,
        features: productForm.features,
        qualification_questions: productForm.qualification_questions,
        department_ids: productForm.department_ids
      };

      if (isCreatingProduct) {
        await axios.post(`${API}/products`, payload);
        toast.success('Product created');
      } else {
        await axios.put(`${API}/products/${selectedProduct.id}`, payload);
        toast.success('Product updated');
      }

      await fetchProducts();
      setIsEditingProduct(false);
      setIsCreatingProduct(false);
    } catch (e) {
      console.error('Save failed:', e);
      toast.error('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = async (productId) => {
    try {
      const { data } = await axios.post(`${API}/products/${productId}/toggle`, {});
      toast.success(data.message);
      await fetchProducts();
    } catch (e) {
      toast.error('Failed to toggle product');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted');
      setSelectedProduct(null);
      await fetchProducts();
    } catch (e) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-panel">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="h-14 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2 flex-shrink-0">
            <img src="/pipeguide-logo.png" alt="PipeGuide" className="h-7 w-7 object-contain" />
            <span className="text-lg font-bold hidden sm:block" style={{ fontFamily: 'var(--font-heading)' }}>
              PipeGuide
            </span>
          </div>
          <div className="flex items-center gap-2">
            <SupportButton />
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 hidden sm:inline-flex text-xs">
              {user?.email}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); navigate('/login'); }}
              className="text-zinc-500 hover:text-zinc-700"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <nav className="flex items-center gap-1 px-4 sm:px-6 pb-2 overflow-x-auto" data-testid="admin-nav">
          <Link to="/admin" className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activeTab ? 'bg-brand-muted text-brand-primary' : 'text-zinc-600 hover:bg-zinc-100'}`} data-testid="nav-admin">
            <Settings className="w-3.5 h-3.5" />
            Settings
          </Link>
          <Link to="/team" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors whitespace-nowrap" data-testid="nav-team">
            <Users className="w-3.5 h-3.5" />
            Team
          </Link>
          <Link to="/billing" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors whitespace-nowrap" data-testid="nav-billing">
            <CreditCard className="w-3.5 h-3.5" />
            Billing
          </Link>
          <Link to="/history" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors whitespace-nowrap" data-testid="nav-history">
            <History className="w-3.5 h-3.5" />
            Calls
          </Link>
          <Link to="/manager" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors whitespace-nowrap" data-testid="nav-manager-insights">
            <BarChart3 className="w-3.5 h-3.5" />
            Insights
          </Link>
          {user?.platform_access?.is_staff && (
          <Link to="/platform" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors whitespace-nowrap" data-testid="nav-platform">
            <Shield className="w-3.5 h-3.5" />
            Platform
          </Link>
          )}
        </nav>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Company Settings
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Products
            </TabsTrigger>
            <TabsTrigger value="frameworks" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Frameworks
            </TabsTrigger>
            <TabsTrigger value="intelligence" className="flex items-center gap-2" data-testid="admin-tab-intelligence">
              <Sparkles className="w-4 h-4" />
              AI Product Intel
            </TabsTrigger>
            <TabsTrigger value="research" className="flex items-center gap-2" data-testid="admin-tab-research">
              <Telescope className="w-4 h-4" />
              Research Template
            </TabsTrigger>
          </TabsList>

          {/* Company Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-zinc-200 shadow-none rounded-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Company Branding
                  </CardTitle>
                  <CardDescription>Customize your company&apos;s appearance in PipeGuide</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Company Name</label>
                    <Input
                      value={companySettings.company_name}
                      onChange={(e) => setCompanySettings(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Your Company Name"
                      className="rounded-md"
                      data-testid="company-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Company Logo</label>
                    {companySettings.logo_url ? (
                      <div className="border border-zinc-200 rounded-md p-4 bg-zinc-50">
                        <div className="flex items-center justify-center mb-3">
                          <img 
                            src={companySettings.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${companySettings.logo_url}` : companySettings.logo_url} 
                            alt="Company logo" 
                            className="max-h-20 object-contain"
                            data-testid="logo-preview"
                            onError={(e) => { e.target.src = ''; e.target.alt = 'Failed to load'; }}
                          />
                        </div>
                        <div className="flex gap-2">
                          <label className="flex-1">
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handleLogoUpload}
                              className="hidden"
                              data-testid="logo-file-input"
                            />
                            <Button 
                              variant="outline" 
                              className="w-full rounded-md" 
                              disabled={uploadingLogo}
                              onClick={(e) => { e.preventDefault(); e.currentTarget.closest('label').querySelector('input').click(); }}
                              data-testid="change-logo-btn"
                            >
                              {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                              {uploadingLogo ? 'Uploading...' : 'Change Logo'}
                            </Button>
                          </label>
                          <Button 
                            variant="outline" 
                            className="rounded-md text-red-600 border-red-200 hover:bg-red-50"
                            onClick={removeLogo}
                            data-testid="remove-logo-btn"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <label className="block" data-testid="logo-upload-area">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={handleLogoUpload}
                          className="hidden"
                          data-testid="logo-file-input"
                        />
                        <div 
                          className="border-2 border-dashed border-zinc-300 rounded-md p-6 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-brand-muted/50 transition-colors"
                          onClick={(e) => { e.currentTarget.closest('label').querySelector('input').click(); }}
                        >
                          {uploadingLogo ? (
                            <Loader2 className="w-8 h-8 text-brand-primary animate-spin mb-2" />
                          ) : (
                            <Upload className="w-8 h-8 text-zinc-400 mb-2" />
                          )}
                          <p className="text-sm font-medium text-zinc-700">
                            {uploadingLogo ? 'Uploading...' : 'Click to upload logo'}
                          </p>
                          <p className="text-xs text-zinc-500 mt-1">JPEG, PNG, WebP or GIF (max 5MB)</p>
                        </div>
                      </label>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Primary Color</label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={companySettings.primary_color}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="w-16 h-10 p-1 rounded-md"
                      />
                      <Input
                        value={companySettings.primary_color}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, primary_color: e.target.value }))}
                        placeholder="#0E4E5A"
                        className="rounded-md flex-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200 shadow-none rounded-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Feature Visibility
                  </CardTitle>
                  <CardDescription>Control which features are visible to sales reps</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-md">
                    <div>
                      <p className="font-medium">Solution Tab</p>
                      <p className="text-sm text-zinc-500">Show the Solution/Products tab during calls</p>
                    </div>
                    <Switch
                      checked={companySettings.show_solution_tab}
                      onCheckedChange={(checked) => setCompanySettings(prev => ({ ...prev, show_solution_tab: checked }))}
                      data-testid="toggle-solution-tab"
                    />
                  </div>

                  {companySettings.show_solution_tab && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Solution Tab Label</label>
                      <Input
                        value={companySettings.solution_tab_label}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, solution_tab_label: e.target.value }))}
                        placeholder="Solution"
                        className="rounded-md"
                        data-testid="solution-tab-label-input"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-md">
                    <div>
                      <p className="font-medium">Managers can view billing</p>
                      <p className="text-sm text-zinc-500">Allow manager seats to see plans, spend, usage and invoices. Purchasing always stays admin-only.</p>
                    </div>
                    <Switch
                      checked={companySettings.managers_can_view_billing || false}
                      onCheckedChange={(checked) => setCompanySettings(prev => ({ ...prev, managers_can_view_billing: checked }))}
                      data-testid="toggle-managers-billing"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200 shadow-none rounded-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Call Type Labels
                  </CardTitle>
                  <CardDescription>Customize how call types are named for your team</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Primary Call Type</label>
                    <Input
                      value={companySettings.call_type_labels?.primary_call || 'Qualification Call'}
                      onChange={(e) => setCompanySettings(prev => ({
                        ...prev,
                        call_type_labels: { ...prev.call_type_labels, primary_call: e.target.value }
                      }))}
                      placeholder="e.g. Discovery Call, Qualification Call"
                      className="rounded-md"
                      data-testid="primary-call-label-input"
                    />
                    <p className="text-xs text-zinc-500">The label shown when starting a new call session</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Follow-up Call Type</label>
                    <Input
                      value={companySettings.call_type_labels?.follow_up_call || 'Follow-up Call'}
                      onChange={(e) => setCompanySettings(prev => ({
                        ...prev,
                        call_type_labels: { ...prev.call_type_labels, follow_up_call: e.target.value }
                      }))}
                      placeholder="e.g. Requirements Gathering, Follow-up Call"
                      className="rounded-md"
                      data-testid="follow-up-call-label-input"
                    />
                    <p className="text-xs text-zinc-500">The label shown for chained follow-up calls</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200 shadow-none rounded-md">
                <CardContent className="pt-6">
                  <Button 
                    onClick={saveCompanySettings} 
                    disabled={savingSettings}
                    className="w-full rounded-md bg-brand-primary hover:bg-brand-hover"
                    data-testid="save-settings-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingSettings ? 'Saving...' : 'Save All Settings'}
                  </Button>
                </CardContent>
              </Card>

              {/* Data Export — POPIA/GDPR portability */}
              <DataExportCard />
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Products List */}
              <div className="lg:col-span-4">
                <Card className="border-zinc-200 shadow-none rounded-md">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Products</CardTitle>
                      <Button size="sm" onClick={handleCreateProduct} className="rounded-md bg-brand-primary hover:bg-brand-hover" data-testid="create-product-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        New
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2 pr-2">
                        {products.length === 0 ? (
                          <p className="text-sm text-zinc-500 text-center py-4">No products yet</p>
                        ) : (
                          products.map(product => (
                            <div
                              key={product.id}
                              onClick={() => handleSelectProduct(product)}
                              className={`p-3 border rounded-md cursor-pointer transition-all ${
                                selectedProduct?.id === product.id ? 'border-brand-primary bg-brand-muted' : 'border-zinc-200 hover:border-zinc-300'
                              } ${!product.is_active ? 'opacity-50' : ''}`}
                              data-testid={`product-item-${product.id}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium">{product.name}</span>
                                {!product.is_active && (
                                  <Badge variant="outline" className="text-xs bg-zinc-100">Hidden</Badge>
                                )}
                              </div>
                              <p className="text-sm text-zinc-500 line-clamp-2">{product.description}</p>
                              <div className="flex gap-2 mt-2 flex-wrap">
                                <Badge variant="outline" className="text-xs">{product.features?.length || 0} features</Badge>
                                <Badge variant="outline" className="text-xs">{product.qualification_questions?.length || 0} questions</Badge>
                                {product.department_ids?.length > 0 ? (
                                  <Badge variant="outline" className="text-xs bg-brand-muted text-brand-primary border-brand-accent">
                                    {product.department_ids.length} dept{product.department_ids.length !== 1 ? 's' : ''}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-zinc-400">All depts</Badge>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Product Editor */}
              <div className="lg:col-span-8">
                {!selectedProduct && !isCreatingProduct ? (
                  <Card className="border-zinc-200 shadow-none rounded-md h-full flex items-center justify-center">
                    <div className="text-center p-8">
                      <Package className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Select a Product</h3>
                      <p className="text-zinc-500 mb-4">Choose a product or create a new one</p>
                      <Button onClick={handleCreateProduct} className="rounded-md">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Product
                      </Button>
                    </div>
                  </Card>
                ) : (
                  <Card className="border-zinc-200 shadow-none rounded-md">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>{isCreatingProduct ? 'New Product' : productForm.name}</CardTitle>
                        <div className="flex gap-2">
                          {!isEditingProduct ? (
                            <>
                              <Button variant="outline" onClick={() => setIsEditingProduct(true)} className="rounded-md" data-testid="edit-product-btn">
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit
                              </Button>
                              <Button variant="outline" onClick={() => toggleProduct(selectedProduct.id)} className="rounded-md">
                                {selectedProduct?.is_active ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                                {selectedProduct?.is_active ? 'Hide' : 'Show'}
                              </Button>
                              <Button variant="outline" onClick={() => deleteProduct(selectedProduct.id)} className="rounded-md text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button variant="outline" onClick={() => { setIsEditingProduct(false); setIsCreatingProduct(false); setSelectedProduct(null); }} className="rounded-md">
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                              </Button>
                              <Button onClick={saveProduct} disabled={loading} className="rounded-md bg-brand-primary hover:bg-brand-hover" data-testid="save-product-btn">
                                <Save className="w-4 h-4 mr-2" />
                                {loading ? 'Saving...' : 'Save'}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Basic Info */}
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Product Name</label>
                          <Input
                            value={productForm.name}
                            onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                            disabled={!isEditingProduct}
                            className="rounded-md"
                            data-testid="product-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Description</label>
                          <Textarea
                            value={productForm.description}
                            onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                            disabled={!isEditingProduct}
                            className="rounded-md resize-none h-20"
                            data-testid="product-description-input"
                          />
                        </div>
                      </div>

                      {/* Features */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium">Features</label>
                        <div className="flex flex-wrap gap-2">
                          {productForm.features.map((feature, i) => (
                            <Badge key={i} variant="outline" className="text-sm px-3 py-1">
                              {feature}
                              {isEditingProduct && (
                                <button onClick={() => handleRemoveFeature(i)} className="ml-2 text-zinc-400 hover:text-red-500">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </Badge>
                          ))}
                        </div>
                        {isEditingProduct && (
                          <div className="flex gap-2">
                            <Input
                              placeholder="Add a feature"
                              value={newFeature}
                              onChange={(e) => setNewFeature(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddFeature()}
                              className="rounded-md"
                              data-testid="new-feature-input"
                            />
                            <Button variant="outline" onClick={handleAddFeature} className="rounded-md">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Qualification Questions */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium">Qualification Questions</label>
                        <p className="text-xs text-zinc-500">Questions to determine if this product fits the prospect&apos;s needs</p>
                        <ScrollArea className="h-[200px]">
                          <div className="space-y-2 pr-2">
                            {productForm.qualification_questions.map((q, i) => (
                              <div key={q.id} className="border border-zinc-200 rounded-md p-3" data-testid={`product-question-${i}`}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <p className="text-sm">{q.question}</p>
                                    <div className="flex gap-2 mt-1">
                                      <Badge variant="outline" className="text-xs">{q.answer_type}</Badge>
                                      <Badge variant="outline" className="text-xs">{q.weight}/5 — {({1:'Nice to Know',2:'Helpful',3:'Important',4:'Critical',5:'Deal Breaker'})[q.weight] || 'Important'}</Badge>
                                    </div>
                                  </div>
                                  {isEditingProduct && (
                                    <button onClick={() => handleRemoveProductQuestion(q.id)} className="text-zinc-400 hover:text-red-500">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                        {isEditingProduct && (
                          <div className="border-t pt-4 space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <Select value={newProductQuestion.answer_type} onValueChange={(v) => setNewProductQuestion(prev => ({ ...prev, answer_type: v }))}>
                                <SelectTrigger className="rounded-md">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="boolean">Yes/No</SelectItem>
                                  <SelectItem value="scale">Scale (1-10)</SelectItem>
                                  <SelectItem value="text">Text</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select value={String(newProductQuestion.weight)} onValueChange={(v) => setNewProductQuestion(prev => ({ ...prev, weight: parseInt(v) }))}>
                                <SelectTrigger className="rounded-md">
                                  <SelectValue placeholder="Weight" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">1 — Nice to Know</SelectItem>
                                  <SelectItem value="2">2 — Helpful</SelectItem>
                                  <SelectItem value="3">3 — Important</SelectItem>
                                  <SelectItem value="4">4 — Critical</SelectItem>
                                  <SelectItem value="5">5 — Deal Breaker</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Textarea
                              placeholder="Enter qualification question..."
                              value={newProductQuestion.question}
                              onChange={(e) => setNewProductQuestion(prev => ({ ...prev, question: e.target.value }))}
                              className="rounded-md resize-none h-16"
                              data-testid="new-product-question-input"
                            />
                            <Button onClick={handleAddProductQuestion} variant="outline" className="w-full rounded-md" data-testid="add-product-question-btn">
                              <Plus className="w-4 h-4 mr-2" />
                              Add Question
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Department Visibility */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium">Department Visibility</label>
                        <p className="text-xs text-zinc-500">
                          {productForm.department_ids.length === 0
                            ? 'Visible to all departments (default)'
                            : `Restricted to ${productForm.department_ids.length} department${productForm.department_ids.length !== 1 ? 's' : ''}`}
                        </p>
                        {departments.length > 0 ? (
                          <div className="space-y-2">
                            {departments.map(dept => {
                              const isSelected = productForm.department_ids.includes(dept.id);
                              return (
                                <label
                                  key={dept.id}
                                  className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                                    isSelected ? 'border-blue-300 bg-brand-muted' : 'border-zinc-200 hover:bg-zinc-50'
                                  } ${!isEditingProduct ? 'opacity-60 pointer-events-none' : ''}`}
                                  data-testid={`dept-visibility-${dept.id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      if (!isEditingProduct) return;
                                      setProductForm(prev => ({
                                        ...prev,
                                        department_ids: isSelected
                                          ? prev.department_ids.filter(id => id !== dept.id)
                                          : [...prev.department_ids, dept.id]
                                      }));
                                    }}
                                    className="rounded border-zinc-300"
                                  />
                                  <span className="text-sm font-medium text-zinc-700">{dept.name}</span>
                                </label>
                              );
                            })}
                            {isEditingProduct && productForm.department_ids.length > 0 && (
                              <button
                                onClick={() => setProductForm(prev => ({ ...prev, department_ids: [] }))}
                                className="text-xs text-brand-primary hover:underline mt-1"
                              >
                                Clear all (make visible to everyone)
                              </button>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-zinc-400">No departments created yet. Create departments in Team Management.</p>
                        )}
                      </div>

                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Frameworks Tab */}
          <TabsContent value="frameworks">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Framework List */}
              <div className="lg:col-span-4">
                <Card className="border-zinc-200 shadow-none rounded-md">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Frameworks</CardTitle>
                      <Button size="sm" onClick={handleCreateFramework} className="rounded-md bg-brand-primary hover:bg-brand-hover" data-testid="create-framework-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        New
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2 pr-2">
                        {frameworks.map(fw => (
                          <div
                            key={fw.id}
                            onClick={() => handleSelectFramework(fw)}
                            className={`p-3 border rounded-md cursor-pointer transition-all ${
                              selectedFramework?.id === fw.id ? 'border-brand-primary bg-brand-muted' : 'border-zinc-200 hover:border-zinc-300'
                            }`}
                            data-testid={`framework-item-${fw.id}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{fw.name}</span>
                              {fw.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                            </div>
                            <p className="text-sm text-zinc-500 line-clamp-2">{fw.description}</p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Framework Editor */}
              <div className="lg:col-span-8">
                {!selectedFramework && !isCreatingFramework ? (
                  <Card className="border-zinc-200 shadow-none rounded-md h-full flex items-center justify-center">
                    <div className="text-center p-8">
                      <Target className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Select a Framework</h3>
                      <p className="text-zinc-500 mb-4">Choose a framework or create a new one</p>
                      <Button onClick={handleCreateFramework} className="rounded-md">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Framework
                      </Button>
                    </div>
                  </Card>
                ) : (
                  <Card className="border-zinc-200 shadow-none rounded-md">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>{isCreatingFramework ? 'New Framework' : frameworkForm.name}</CardTitle>
                        <div className="flex gap-2">
                          {!isEditingFramework ? (
                            <>
                              <Button variant="outline" onClick={() => setIsEditingFramework(true)} className="rounded-md">
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit
                              </Button>
                              {selectedFramework && !selectedFramework.is_default && (
                                <Button variant="outline" onClick={() => deleteFramework(selectedFramework.id)} className="rounded-md text-red-600">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          ) : (
                            <>
                              <Button variant="outline" onClick={() => { setIsEditingFramework(false); setIsCreatingFramework(false); }} className="rounded-md">
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                              </Button>
                              <Button onClick={saveFramework} disabled={loading} className="rounded-md bg-brand-primary hover:bg-brand-hover">
                                <Save className="w-4 h-4 mr-2" />
                                {loading ? 'Saving...' : 'Save'}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Name</label>
                          <Input
                            value={frameworkForm.name}
                            onChange={(e) => setFrameworkForm(prev => ({ ...prev, name: e.target.value }))}
                            disabled={!isEditingFramework}
                            className="rounded-md"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Description</label>
                          <Input
                            value={frameworkForm.description}
                            onChange={(e) => setFrameworkForm(prev => ({ ...prev, description: e.target.value }))}
                            disabled={!isEditingFramework}
                            className="rounded-md"
                          />
                        </div>
                      </div>

                      {/* Categories */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium">Categories</label>
                        <div className="flex flex-wrap gap-2">
                          {frameworkForm.categories.map((cat, i) => (
                            <Badge key={i} variant="outline" className="text-sm px-3 py-1">
                              {cat}
                              {isEditingFramework && (
                                <button onClick={() => handleRemoveCategory(cat)} className="ml-2 text-zinc-400 hover:text-red-500">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </Badge>
                          ))}
                        </div>
                        {isEditingFramework && (
                          <div className="flex gap-2">
                            <Input
                              placeholder="New category"
                              value={newCategory}
                              onChange={(e) => setNewCategory(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                              className="rounded-md"
                            />
                            <Button variant="outline" onClick={handleAddCategory} className="rounded-md">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Questions */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium">Questions ({frameworkForm.questions.length})</label>
                        <ScrollArea className="h-[200px]">
                          <div className="space-y-2 pr-2">
                            {frameworkForm.questions.map((q, i) => (
                              <div key={q.id} className="border border-zinc-200 rounded-md p-3">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <Badge variant="outline" className="text-xs mb-1">{q.category}</Badge>
                                    <p className="text-sm">{q.question}</p>
                                    <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${
                                      q.weight >= 5 ? 'bg-red-100 text-red-700' :
                                      q.weight >= 4 ? 'bg-orange-100 text-orange-700' :
                                      q.weight >= 3 ? 'bg-brand-accent-muted text-brand-primary' :
                                      q.weight >= 2 ? 'bg-zinc-100 text-zinc-600' :
                                      'bg-zinc-50 text-zinc-500'
                                    }`}>
                                      {q.weight}/5 — {({1:'Nice to Know',2:'Helpful',3:'Important',4:'Critical',5:'Deal Breaker'})[q.weight] || 'Important'}
                                    </span>
                                  </div>
                                  {isEditingFramework && (
                                    <button onClick={() => handleRemoveQuestion(q.id)} className="text-zinc-400 hover:text-red-500">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                        {isEditingFramework && frameworkForm.categories.length > 0 && (
                          <div className="border-t pt-4 space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <Select value={newQuestion.category} onValueChange={(v) => setNewQuestion(prev => ({ ...prev, category: v }))}>
                                <SelectTrigger className="rounded-md">
                                  <SelectValue placeholder="Category" />
                                </SelectTrigger>
                                <SelectContent>
                                  {frameworkForm.categories.map((cat, i) => (
                                    <SelectItem key={i} value={cat}>{cat}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select value={String(newQuestion.weight)} onValueChange={(v) => setNewQuestion(prev => ({ ...prev, weight: parseInt(v) }))}>
                                <SelectTrigger className="rounded-md">
                                  <SelectValue placeholder="Weight" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">1 — Nice to Know</SelectItem>
                                  <SelectItem value="2">2 — Helpful</SelectItem>
                                  <SelectItem value="3">3 — Important</SelectItem>
                                  <SelectItem value="4">4 — Critical</SelectItem>
                                  <SelectItem value="5">5 — Deal Breaker</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Textarea
                              placeholder="Enter question..."
                              value={newQuestion.question}
                              onChange={(e) => setNewQuestion(prev => ({ ...prev, question: e.target.value }))}
                              className="rounded-md resize-none h-16"
                            />
                            <Button onClick={handleAddQuestion} variant="outline" className="w-full rounded-md">
                              <Plus className="w-4 h-4 mr-2" />
                              Add Question
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* AI Product Intelligence Tab */}
          <TabsContent value="intelligence">
            <ProductIntelligence frameworks={frameworks} />
          </TabsContent>

          {/* Research Template Tab */}
          <TabsContent value="research">
            <ResearchTemplateEditor />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// =========================================================================
// DataExportCard — admin-only "Download all my data" (POPIA/GDPR portability)
// =========================================================================
function DataExportCard() {
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfDays, setPdfDays] = useState(30);

  const downloadBlob = async (url, fallbackName) => {
    const response = await axios.get(url, { responseType: 'blob' });
    const cd = response.headers['content-disposition'] || '';
    const match = cd.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : fallbackName;
    const blobType = response.headers['content-type'] || 'application/octet-stream';
    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: blobType }));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const download = async () => {
    setDownloading(true);
    try {
      await downloadBlob(`${API}/org/export-data`, `pipeguide-export-${Date.now()}.zip`);
      toast.success('Data export downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export data');
    } finally {
      setDownloading(false);
    }
  };

  const downloadHighlights = async () => {
    setDownloadingPdf(true);
    try {
      await downloadBlob(`${API}/org/highlights-pdf?days=${pdfDays}`, `pipeguide-highlights-${Date.now()}.pdf`);
      toast.success('Call Highlights PDF downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate highlights PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Card className="border-zinc-200 shadow-none rounded-md mt-6" data-testid="data-export-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Download className="w-4 h-4 text-brand-primary" />
          Download Your Data
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Full data export */}
        <div>
          <h4 className="text-sm font-semibold text-zinc-900 mb-1">Full data export (.zip)</h4>
          <p className="text-sm text-zinc-600 mb-3">
            Export a single ZIP containing everything your organisation owns on PipeGuide:
            team members, every call session, full transcripts, AI-generated summaries,
            framework configuration, and billing state. Satisfies POPIA / GDPR
            data-portability rights and is useful before any account change.
          </p>
          <details className="text-xs text-zinc-500 mb-3">
            <summary className="cursor-pointer font-medium hover:text-zinc-700">What&apos;s included?</summary>
            <ul className="list-disc pl-5 mt-2 space-y-0.5">
              <li><span className="font-mono">organization.json</span> — org profile + lifecycle status</li>
              <li><span className="font-mono">subscription.json</span> — plan + billing state</li>
              <li><span className="font-mono">framework.json</span> — your qualification framework</li>
              <li><span className="font-mono">departments.json</span> — team structure</li>
              <li><span className="font-mono">users.csv</span> — all team members</li>
              <li><span className="font-mono">sessions.csv</span> — every call, scores, durations</li>
              <li><span className="font-mono">transcripts/</span> — one .txt file per call</li>
              <li><span className="font-mono">summaries/</span> — one .json file per call</li>
            </ul>
          </details>
          <Button
            onClick={download}
            disabled={downloading}
            variant="outline"
            className="rounded-md text-brand-primary border-brand-primary/30 hover:bg-brand-muted"
            data-testid="export-data-btn"
          >
            {downloading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing export…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Download data export (.zip)</>
            )}
          </Button>
        </div>

        {/* Call Highlights PDF */}
        <div className="border-t border-zinc-100 pt-5">
          <h4 className="text-sm font-semibold text-zinc-900 mb-1">Call Highlights PDF (executive summary)</h4>
          <p className="text-sm text-zinc-600 mb-3">
            A single branded PDF aggregating your team&apos;s last 30 days into a leadership-ready
            summary: activity stats, qualification themes, top deals, per-rep snapshot, and the
            most-suggested AI questions. Perfect handout for monthly QBRs.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-zinc-700">Period:</label>
            <Select value={String(pdfDays)} onValueChange={(v) => setPdfDays(Number(v))}>
              <SelectTrigger className="rounded-md h-9 w-32" data-testid="pdf-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={downloadHighlights}
              disabled={downloadingPdf}
              variant="outline"
              className="rounded-md text-brand-primary border-brand-primary/30 hover:bg-brand-muted"
              data-testid="export-highlights-btn"
            >
              {downloadingPdf ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Download Highlights PDF</>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

